import { useAudioStore } from "../state/useAudioStore";

interface SliderRowProps {
  label: string;
  value: number;
  muted: boolean;
  onChange: (v: number) => void;
  onToggle: () => void;
  hint?: string;
}

function SliderRow({ label, value, muted, onChange, onToggle, hint }: SliderRowProps) {
  return (
    <div className="audio-row">
      <div className="audio-row-head">
        <button
          type="button"
          className={`audio-mute ${muted ? "muted" : ""}`}
          onClick={onToggle}
          aria-label={muted ? "Ton einschalten" : "Stummschalten"}
        >
          {muted ? "🔇" : value === 0 ? "🔈" : value < 0.5 ? "🔉" : "🔊"}
        </button>
        <div className="audio-label-block">
          <span className="audio-label">{label}</span>
          {hint && <span className="audio-hint">{hint}</span>}
        </div>
        <span className="audio-value">{muted ? "stumm" : `${Math.round(value * 100)}%`}</span>
      </div>
      <input
        type="range"
        min={0}
        max={100}
        step={1}
        value={Math.round(value * 100)}
        onChange={(e) => onChange(Number(e.target.value) / 100)}
        disabled={muted}
        className="audio-slider"
        aria-label={`${label} Lautstärke`}
      />
    </div>
  );
}

interface AudioSettingsPanelProps {
  showVoice?: boolean;
}

export function AudioSettingsPanel({ showVoice = false }: AudioSettingsPanelProps) {
  // Zustand v5 verlangt für stabile Re-Renders einzelne Selektoren –
  // ein destrukturiertes `useStore()` ohne Selector liefert zwar die Werte,
  // löst aber nicht zuverlässig bei jedem Store-Change ein Re-Render aus.
  const musicVolume = useAudioStore((s) => s.musicVolume);
  const sfxVolume = useAudioStore((s) => s.sfxVolume);
  const voiceVolume = useAudioStore((s) => s.voiceVolume);
  const musicMuted = useAudioStore((s) => s.musicMuted);
  const sfxMuted = useAudioStore((s) => s.sfxMuted);
  const voiceMuted = useAudioStore((s) => s.voiceMuted);
  const setMusicVolume = useAudioStore((s) => s.setMusicVolume);
  const setSfxVolume = useAudioStore((s) => s.setSfxVolume);
  const setVoiceVolume = useAudioStore((s) => s.setVoiceVolume);
  const toggleMusic = useAudioStore((s) => s.toggleMusic);
  const toggleSfx = useAudioStore((s) => s.toggleSfx);
  const toggleVoice = useAudioStore((s) => s.toggleVoice);
  const muteAll = useAudioStore((s) => s.muteAll);

  const allMuted = musicMuted && sfxMuted && (!showVoice || voiceMuted);

  return (
    <div className="audio-panel">
      <div className="audio-panel-head">
        <h3>Ton</h3>
        <button
          type="button"
          className="btn btn-ghost audio-master"
          onClick={() => muteAll(!allMuted)}
        >
          {allMuted ? "Alle einschalten" : "Alle stummschalten"}
        </button>
      </div>

      <SliderRow
        label="Hintergrundmusik"
        value={musicVolume}
        muted={musicMuted}
        onChange={setMusicVolume}
        onToggle={toggleMusic}
      />
      <SliderRow
        label="Soundeffekte"
        value={sfxVolume}
        muted={sfxMuted}
        onChange={setSfxVolume}
        onToggle={toggleSfx}
        hint="Sprung, Monster, Princess gerettet …"
      />
      {showVoice && (
        <SliderRow
          label="Voice-Chat"
          value={voiceVolume}
          muted={voiceMuted}
          onChange={setVoiceVolume}
          onToggle={toggleVoice}
          hint="Lautstärke deiner Mitspieler"
        />
      )}
    </div>
  );
}
